import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useTerms } from '../../layout/ShellStatus';
import { Icon } from '../../design/Icon';
import { ErrorState, Spinner } from '../../components/shared/States';
import { HelpArticleBody } from '../../help/HelpArticle';
import { useHelpContent } from '../../help/HelpContext';
import { ROLE_NAMES, neighbours, searchHelp } from '../../help/helpData';
import '../../help/help.css';

const ROLE_STARTS = [
  { id: 'start-supervisors', icon: 'headset', title: 'Supervisors', text: 'Watch queues live, follow calls and coach your team.' },
  { id: 'start-managers', icon: 'chart', title: 'Managers and executives', text: 'Service level, trends and reports for your divisions.' },
  { id: 'start-admins', icon: 'userCog', title: 'Administrators', text: (terms) => `Set up divisions, ${terms.communitiesLower}, users, AI and targets.` },
];

function SearchBox({ value, onChange, onSubmit, autoFocus, large }) {
  return (
    <form
      className={`help-search${large ? ' help-search-large' : ''}`}
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.(value);
      }}
    >
      <label>
        <span className="sr-only">Search help</span>
        <Icon name="search" />
        <input type="search" placeholder="Search help articles" value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus} />
      </label>
    </form>
  );
}

function Sidebar({ help, activeId, activeCategory }) {
  const [expanded, setExpanded] = useState(() => new Set(activeCategory ? [activeCategory] : []));
  useEffect(() => {
    if (activeCategory) setExpanded((s) => new Set([...s, activeCategory]));
  }, [activeCategory]);
  const toggle = (id) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <nav className="help-sidebar" aria-label="Help topics">
      <Link to="/help" className={`help-sidebar-home${!activeId && !activeCategory ? ' active' : ''}`}>
        <Icon name="home" /> Help Center home
      </Link>
      {help.categories.map((category) => {
        const open = expanded.has(category.id);
        const articles = help.byCategory.get(category.id) || [];
        return (
          <div key={category.id} className="help-sidebar-group">
            <button type="button" className="help-sidebar-cat" aria-expanded={open} onClick={() => toggle(category.id)}>
              <Icon name={open ? 'chevD' : 'chevR'} className="ico-sm" />
              <span>{category.title}</span>
            </button>
            {open && (
              <ul>
                {articles.map((a) => (
                  <li key={a.id}>
                    <Link to={`/help/${a.id}`} className={a.id === activeId ? 'active' : undefined} aria-current={a.id === activeId ? 'page' : undefined}>
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function Home({ help }) {
  const terms = useTerms();
  return (
    <div className="help-home">
      <div className="help-roles">
        {ROLE_STARTS.filter((r) => help.byId.has(r.id)).map((r) => (
          <Link key={r.id} to={`/help/${r.id}`} className="card help-role-card">
            <Icon name={r.icon} />
            <span className="help-link-title">Start here: {r.title}</span>
            <span className="help-link-sub">{typeof r.text === 'function' ? r.text(terms) : r.text}</span>
          </Link>
        ))}
      </div>
      <div className="help-category-grid">
        {help.categories.map((category) => {
          const articles = help.byCategory.get(category.id) || [];
          return (
            <section key={category.id} className="card help-category-card" aria-labelledby={`help-cat-${category.id}`}>
              <div className="help-category-head">
                <span className="help-category-icon">
                  <Icon name={category.icon} />
                </span>
                <div>
                  <h2 id={`help-cat-${category.id}`} className="help-category-title">
                    {category.title}
                  </h2>
                  <p className="help-link-sub">{category.description}</p>
                </div>
              </div>
              <ul className="help-category-links">
                {articles.map((a) => (
                  <li key={a.id}>
                    <Link to={`/help/${a.id}`}>{a.title}</Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function Results({ help, query }) {
  const results = useMemo(() => searchHelp(help, query, 30), [help, query]);
  return (
    <section className="help-results" aria-live="polite">
      <h2 className="section-title">{results.length ? `${results.length} result${results.length === 1 ? '' : 's'} for “${query}”` : `No results for “${query}”`}</h2>
      {results.length === 0 && <p className="help-muted">Try fewer or different words, or browse the topics on the left.</p>}
      <ul className="help-link-list">
        {results.map(({ article, snippet }) => (
          <li key={article.id}>
            <Link to={`/help/${article.id}`} className="help-link-row">
              <span className="help-link-title">{article.title}</span>
              <span className="help-link-sub">{snippet}</span>
              <span className="help-result-cat">{help.categories.find((c) => c.id === article.category)?.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ArticleView({ help, article }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { previous, next } = neighbours(help, article);
  const related = article.related.map((id) => help.byId.get(id)).filter(Boolean);

  useEffect(() => {
    const anchor = location.hash.slice(1);
    if (anchor) setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ block: 'start' }), 50);
    else window.scrollTo?.(0, 0);
  }, [article.id, location.hash]);

  const onLink = (node) => navigate(node.kind === 'app' ? node.href : `/help/${node.href}${node.anchor ? `#${node.anchor}` : ''}`);
  const roles = article.roles.filter((r) => r !== 'everyone').map((r) => ROLE_NAMES[r]);

  return (
    <div className="help-article-layout">
      <article className="help-article" aria-label={article.title}>
        <p className="help-article-summary">{article.summary}</p>
        {roles.length > 0 && (
          <p className="help-article-meta">
            <Icon name="users" className="ico-sm" /> For {roles.join(', ').toLowerCase()}
          </p>
        )}
        <HelpArticleBody article={article} onLink={onLink} />
        {related.length > 0 && (
          <section className="help-related" aria-label="Related articles">
            <h2>Related articles</h2>
            <ul className="help-link-list">
              {related.map((r) => (
                <li key={r.id}>
                  <Link to={`/help/${r.id}`} className="help-link-row">
                    <span className="help-link-title">{r.title}</span>
                    <span className="help-link-sub">{r.summary}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        <nav className="help-pager" aria-label="Previous and next articles">
          {previous ? (
            <Link to={`/help/${previous.id}`} className="help-pager-link">
              <span className="help-muted">Previous</span>
              <span>{previous.title}</span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={`/help/${next.id}`} className="help-pager-link help-pager-next">
              <span className="help-muted">Next</span>
              <span>{next.title}</span>
            </Link>
          ) : (
            <span />
          )}
        </nav>
        {help.updated && <p className="help-muted help-updated">Last updated {new Date(`${help.updated}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>}
      </article>
      {article.headings.length > 1 && (
        <nav className="help-toc" aria-label="On this page">
          <p className="help-toc-title">On this page</p>
          <ul>
            {article.headings.map((h) => (
              <li key={h.id}>
                <a
                  href={`#${h.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/help/${article.id}#${h.id}`, { replace: true });
                  }}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

/** Help Center: topics, search and articles compiled from docs/user-guide (tools/user-guide/build-help.js). */
export function HelpCenterPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { help, error, retry } = useHelpContent();
  const query = params.get('q') || '';
  const [draft, setDraft] = useState(query);
  useEffect(() => setDraft(query), [query]);

  const article = help && articleId ? help.byId.get(articleId) : null;
  const category = article && help ? help.categories.find((c) => c.id === article.category) : null;

  const updateQuery = (value) => {
    setDraft(value);
    setParams(value.trim() ? { q: value } : {}, { replace: true });
  };

  let title = 'Help Center';
  let crumbs;
  if (article) {
    title = article.title;
    crumbs = [{ label: 'Help Center', to: '/help' }, { label: category?.title || '' }];
  } else if (query) {
    crumbs = [{ label: 'Help Center', to: '/help' }, { label: 'Search' }];
  }

  let body;
  if (error && !help) body = <ErrorState title="Help couldn't load" body="Check your connection and try again." onRetry={retry} />;
  else if (!help) body = <Spinner label="Loading help…" />;
  else if (articleId && !article) {
    body = (
      <div className="help-empty">
        <p>That article doesn't exist or has moved.</p>
        <Link to="/help" className="btn">
          Go to Help Center home
        </Link>
      </div>
    );
  } else if (query && !article) body = <Results help={help} query={query} />;
  else if (article) body = <ArticleView help={help} article={article} />;
  else body = <Home help={help} />;

  return (
    <Page
      title={title}
      documentTitle={article ? `${article.title} | Help` : 'Help Center'}
      subtitle={!article && !query ? 'Guides for using and setting up Vertolytics. Press ? on any page to open help without leaving it.' : undefined}
      crumbs={crumbs}
      actions={
        <SearchBox
          value={draft}
          onChange={(value) => (article ? setDraft(value) : updateQuery(value))}
          onSubmit={(value) => value.trim() && navigate(`/help?q=${encodeURIComponent(value.trim())}`)}
        />
      }
    >
      {help ? (
        <div className="help-center">
          <Sidebar help={help} activeId={article?.id} activeCategory={article?.category} />
          <div className="help-center-main">{body}</div>
        </div>
      ) : (
        body
      )}
    </Page>
  );
}
